import React from 'react';
import {
  FileText, ClipboardList, Hourglass, Search, Sparkles, AlertTriangle,
  CheckCircle2, PauseCircle, AlertCircle, Ban,
} from 'lucide-react';

const ComplianceStatusCard = ({ status, compact = false }) => {
  const getStatusConfig = (workflowState) => {
    const configs = {
      account_created: {
        label: 'Account Created',
        color: 'blue',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-400',
        textColor: 'text-blue-800',
        icon: FileText,
        message: 'Complete your application to get started.',
        progress: 10,
      },
      application_started: {
        label: 'Application In Progress',
        color: 'blue',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-400',
        textColor: 'text-blue-800',
        icon: ClipboardList,
        message: 'Continue filling out your application.',
        progress: 25,
      },
      application_submitted: {
        label: 'Under Review',
        color: 'yellow',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-400',
        textColor: 'text-yellow-800',
        icon: Hourglass,
        message: 'Your application is being reviewed.',
        progress: 40,
      },
      under_review: {
        label: 'Under Review',
        color: 'yellow',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-400',
        textColor: 'text-yellow-800',
        icon: Search,
        message: 'Admin is reviewing your submission.',
        progress: 50,
      },
      conditional_approval: {
        label: 'Conditionally Approved',
        color: 'indigo',
        bgColor: 'bg-indigo-50',
        borderColor: 'border-indigo-400',
        textColor: 'text-indigo-800',
        icon: Sparkles,
        message: 'Complete compliance documents to activate.',
        progress: 60,
      },
      compliance_incomplete: {
        label: 'Compliance Incomplete',
        color: 'orange',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-400',
        textColor: 'text-orange-800',
        icon: AlertTriangle,
        message: 'Upload missing compliance documents.',
        progress: 70,
      },
      compliance_under_review: {
        label: 'Documents Under Review',
        color: 'yellow',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-400',
        textColor: 'text-yellow-800',
        icon: FileText,
        message: 'Your documents are being verified.',
        progress: 85,
      },
      active: {
        label: 'Active',
        color: 'green',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-400',
        textColor: 'text-green-800',
        icon: CheckCircle2,
        message: 'You are fully approved and active!',
        progress: 100,
      },
      suspended: {
        label: 'Suspended',
        color: 'red',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-400',
        textColor: 'text-red-800',
        icon: PauseCircle,
        message: 'Your account has been suspended.',
        progress: 0,
      },
      expired_docs: {
        label: 'Expired Documents',
        color: 'red',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-400',
        textColor: 'text-red-800',
        icon: AlertCircle,
        message: 'Some documents have expired. Please renew.',
        progress: 70,
      },
      terminated: {
        label: 'Terminated',
        color: 'gray',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-400',
        textColor: 'text-gray-800',
        icon: Ban,
        message: 'Your account has been terminated.',
        progress: 0,
      },
    };
    return configs[workflowState] || configs.account_created;
  };

  const config = getStatusConfig(status);
  const Icon = config.icon;

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bgColor} ${config.textColor}`}
           data-testid="compliance-status-compact">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{config.label}</span>
      </div>
    );
  }

  return (
    <div className={`${config.bgColor} border-l-4 ${config.borderColor} rounded-r-lg p-4 sm:p-5`}
         data-testid="compliance-status-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1">
          <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${config.textColor}`} />
          <div>
            <h3 className={`font-semibold ${config.textColor}`}>
              {config.label}
            </h3>
            <p className={`text-sm ${config.textColor} opacity-80 mt-0.5`}>
              {config.message}
            </p>
          </div>
        </div>
        
        {/* Progress indicator for non-terminal states */}
        {config.progress > 0 && config.progress < 100 && (
          <div className="sm:w-32">
            <div className="flex justify-between text-xs mb-1">
              <span className={config.textColor}>Progress</span>
              <span className={config.textColor}>{config.progress}%</span>
            </div>
            <div className="h-2 bg-white/50 rounded-full overflow-hidden">
              <div 
                className={`h-full bg-${config.color}-500 rounded-full transition-all duration-500`}
                style={{ width: `${config.progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceStatusCard;
